// Receive Stock entry — desktop PurchaseOrderWindow/PurchaseWindow parity.
//
// Supplier header + 13-column line grid (Qty / Pk / Bonus / Cost / Batch /
// Expiry / Disc / GST / Tot / S/Price). Posting (F10 in the desktop) saves a
// Receive (QOH up, cost + selling-price update, PO knock-off) via
// SavePurchases method="invoice"; Hold saves a SuspendedReceive
// (method="suspend", reserves qohHoldIn). Recall loads an on-hold record and
// releases it only after the new result is saved. Edit on a posted receive
// loads it with its id and posts with that id, so the store updates the
// existing record (stock movement reversed and re-applied) instead of
// creating a second receive.

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type {
  PosStockItem,
  PosPack,
  TradePurchaseIn,
  TradePurchaseRow,
  TradeServerKey,
} from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { isRetailProfile } from '../lib/site-config.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

/** Retail-stall deployment — one packing per item, so hide the pack chooser. */
const RETAIL = isRetailProfile();

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmtMoney = (n: number | undefined) => (Number(n) || 0).toFixed(2);

function todayIso(): string {
  const d = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local-time yyyy-mm-dd for a timestamp (never the UTC-shifted date). */
function isoFromMs(ms: number): string {
  const d = new Date(ms);
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface RecLine {
  stockId: string;
  code: string;
  name: string;
  pack: string;
  packDesc: string;
  packSize: number;
  quantity: number;
  bonusQty: number;
  price: number;
  discount: number;
  srp: number;
  taxPct: number;
  tax: number;
  value: number;
  batchNo: string;
  expiryMs: number;
  qoh: number;
}

interface KeptRecView {
  lines: RecLine[];
  supplierId: string;
  supplierName: string;
  supplierCode: string;
  refNo: string;
  term: string;
  note: string;
  date: string;
  // edit/recall context — must survive tab/outlet round-trips, otherwise
  // posting a restored edit would create a NEW receive, and a restored hold
  // recall would leave the old hold behind
  editingId: string;
  recallHoldId: string | null;
  loadedTxnMs: number;
}

interface PartyOption { id: string; description: string; }

@customElement('receive-entry')
export class ReceiveEntry extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .rec-wrap { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 0.75rem 1rem 1rem; gap: 0.6rem; }
    .head-row { display: flex; flex-wrap: wrap; gap: 0.6rem; align-items: flex-end; flex-shrink: 0; }
    .head-row .fg { margin-bottom: 0; }
    .head-row .grow { flex: 1; min-width: 180px; }
    .party-chip { display: inline-flex; align-items: center; gap: 0.5rem; padding: 6px 12px; border-radius: 999px; border: 1px solid var(--color-border); background: var(--color-surface); font-size: 0.82rem; min-height: 40px; }
    .party-chip button { border: none; background: none; cursor: pointer; color: var(--color-text-secondary); font-size: 1rem; line-height: 1; }

    .add-row { display: flex; gap: 0.5rem; flex-shrink: 0; position: relative; }
    .add-row input { flex: 1; }
    .search-pop { position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 60; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 12px; max-height: 300px; overflow-y: auto; box-shadow: 0 16px 40px rgba(15,35,80,0.18); }
    .search-item { display: block; width: 100%; text-align: left; min-height: 44px; padding: 8px 12px; background: none; border: none; border-bottom: 1px solid var(--color-border); cursor: pointer; font-family: inherit; }
    .search-item:last-child { border-bottom: none; }
    .search-item:hover { background: #f4f7fb; }
    .si-name { display: block; font-size: 0.84rem; color: var(--color-text); }
    .si-sub { display: block; font-size: 0.7rem; color: var(--color-text-secondary); }

    .grid-wrap { flex: 1; min-height: 0; overflow: auto; border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); }
    .grid-wrap table { min-width: 1080px; }
    .grid-wrap thead th { position: sticky; top: 0; z-index: 1; background: var(--color-surface); border-bottom: none; box-shadow: inset 0 -1px 0 var(--color-border); }
    td input { min-height: 32px; }
    .cell-num input { text-align: right; }
    .empty { padding: 2rem 1rem; text-align: center; color: var(--color-text-secondary); font-size: 0.85rem; }
    .rm { border: none; background: none; color: var(--color-error, #d93025); font-size: 1rem; cursor: pointer; }
    .footer-bar { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: flex-end; flex-shrink: 0; }
    .totals-box { display: flex; gap: 1.2rem; flex-wrap: wrap; align-items: flex-end; background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 14px; padding: 0.6rem 1rem; }
    .totals-box .t { text-align: right; min-width: 96px; }
    .totals-box .t .lbl { display: block; font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--color-text-secondary); font-weight: 700; }
    .totals-box .t .val { font-size: 0.95rem; font-weight: 600; }
    .totals-box .t.grand .val { font-size: 1.15rem; color: var(--color-primary); }
    .notice { font-size: 0.8rem; color: var(--color-success, #188038); }
    .err { font-size: 0.8rem; color: var(--color-error, #d93025); }

    .modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 1rem; }
    .modal { background: var(--color-surface); border-radius: 16px; width: 100%; max-width: 620px; max-height: 88dvh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 24px 60px rgba(15,35,80,0.3); }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-close { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: none; border: none; border-radius: 10px; font-size: 1.2rem; cursor: pointer; color: var(--color-text-secondary); }
    .modal-close:hover { background: #f4f7fb; }
    .modal-body { padding: 1rem 1.1rem; overflow-y: auto; }
    .modal-footer { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .pick-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 0.6rem 0.9rem; }
    .pick-grid .fg { margin-bottom: 0; }
    .pay-line { display: flex; justify-content: space-between; align-items: center; padding: 0.45rem 0; border-bottom: 1px dashed var(--color-border); font-size: 0.9rem; }
    .pay-line.grand { border-bottom: none; font-weight: 700; font-size: 1.05rem; }
    @media (max-width: 768px) {
      .modal-overlay { align-items: flex-end; padding: 0; }
      .modal { max-width: none; border-radius: 18px 18px 0 0; max-height: 92dvh; }
      .modal-footer { padding-bottom: calc(0.9rem + env(safe-area-inset-bottom)); }
    }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: Object }) recallRow: TradePurchaseRow | null = null;
  @property({ type: Number }) recallToken = 0;
  /** True when recallRow is a POSTED receive being edited (not a hold recall). */
  @property({ type: Boolean }) editMode = false;

  @state() private _sk: TradeServerKey | null = null;
  @state() private _skStore = '';
  @state() private _skFailed = '';
  @state() private _initError = '';
  @state() private _lines: RecLine[] = [];
  @state() private _error = '';
  @state() private _notice = '';
  @state() private _busy = false;

  @state() private _supplierId = '';
  @state() private _supplierName = '';
  @state() private _supplierCode = '';
  @state() private _refNo = '';
  @state() private _term = '0';
  @state() private _note = '';
  @state() private _date = todayIso();

  @state() private _searchTerm = '';
  @state() private _searchResults: PosStockItem[] = [];
  @state() private _searchBusy = false;
  @state() private _searchOpen = false;

  @state() private _pick: PosStockItem | null = null;
  @state() private _pickQty = '1';
  @state() private _pickPack = '';
  @state() private _pickPrice = '';
  @state() private _pickDisc = '';
  @state() private _pickBonusQty = '';
  @state() private _pickBatch = '';
  @state() private _pickExpiry = '';
  @state() private _pickSrp = '';

  @state() private _partyOpen = false;
  @state() private _partySearch = '';
  @state() private _partyResults: PartyOption[] = [];
  @state() private _partyBusy = false;

  @state() private _savedId = '';
  @state() private _savedUpdated = false;

  /** Posted receive being edited (sends its id on post); empty for a new one. */
  @state() private _editingId = '';
  private _recallHoldId: string | null = null;
  private _lastRecallToken = 0;
  /** Original transaction timestamp loaded with a recall/edit (0 = none). */
  private _loadedTxnMs = 0;
  private _searchTimer: number | undefined;

  private _view = new OutletViewCache<KeptRecView>(this, 'trade-receive', {
    capture: () => ({
      lines: this._lines,
      supplierId: this._supplierId,
      supplierName: this._supplierName,
      supplierCode: this._supplierCode,
      refNo: this._refNo,
      term: this._term,
      note: this._note,
      date: this._date,
      editingId: this._editingId,
      recallHoldId: this._recallHoldId,
      loadedTxnMs: this._loadedTxnMs,
    }),
    restore: (v) => {
      this._lines = v.lines;
      this._supplierId = v.supplierId;
      this._supplierName = v.supplierName;
      this._supplierCode = v.supplierCode;
      this._refNo = v.refNo;
      this._term = v.term;
      this._note = v.note;
      this._date = v.date ?? todayIso();
      this._editingId = v.editingId ?? '';
      this._recallHoldId = v.recallHoldId ?? null;
      this._loadedTxnMs = v.loadedTxnMs ?? 0;
      this._busy = false;
      this._pick = null;
      this._searchOpen = false;
      this._initError = '';
    },
    reset: () => {
      this._clearAll();
      this._initError = '';
    },
  });

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._searchTimer !== undefined) window.clearTimeout(this._searchTimer);
    this._view.dispose();
  }

  protected updated() {
    this._view.switchTo(this.storeCode ? this.storeCode : '');
    // Store settings are per outlet: fetch whenever the current outlet has
    // none yet. A failed outlet is NOT auto-retried (avoids loops); the
    // Retry button clears _skFailed to allow another attempt.
    if (this.storeCode && this._skStore !== this.storeCode && this._skFailed !== this.storeCode) {
      void this._init();
    }
    if (this.recallRow && this.recallToken !== this._lastRecallToken) {
      this._lastRecallToken = this.recallToken;
      this._loadRecall(this.recallRow, this.editMode);
    }
  }

  private async _init() {
    if (!this.storeCode) return;
    const store = this.storeCode;
    const epoch = this._view.epoch;
    this._initError = '';
    try {
      const data = (await api.tradeServerKey(store)).data;
      if (!this._view.isCurrent(epoch) || this.storeCode !== store) return;
      this._sk = data;
      this._skStore = store;
      this._skFailed = '';
    } catch (e: any) {
      if (!this._view.isCurrent(epoch) || this.storeCode !== store) return;
      this._skFailed = store;
      this._initError = e?.message || 'Failed to load store settings';
    }
  }

  private _clearAll() {
    this._lines = [];
    this._supplierId = '';
    this._supplierName = '';
    this._supplierCode = '';
    this._refNo = '';
    this._term = '0';
    this._note = '';
    this._date = todayIso();
    this._error = '';
    this._recallHoldId = null;
    this._editingId = '';
    this._loadedTxnMs = 0;
  }

  // ── pricing ───────────────────────────────────────────────────────────

  private _defaultCost(item: PosStockItem): number {
    switch (this._sk?.purchase.defaultCostPrice) {
      case 'latestCost': return item.latestCost ?? item.weightedAvgCost ?? 0;
      case 'maxCost': return item.maxCost ?? item.weightedAvgCost ?? 0;
      default: return item.weightedAvgCost ?? 0;
    }
  }

  private _taxPct(item: PosStockItem): number {
    const gstType = this._sk?.gstTypeId;
    if (!gstType) return 0;
    const now = Date.now();
    const t = (item.taxAndCharges ?? []).find(
      (x) => x.typeId === gstType && (!x.fromDate || now >= x.fromDate) && (!x.toDate || now <= x.toDate));
    return t ? t.percentage / 100 : 0;
  }

  private _recalc(line: RecLine) {
    const qty = Number(line.quantity) || 0;
    const net = qty * (Number(line.price) || 0) - (Number(line.discount) || 0);
    line.tax = round2(net * line.taxPct);
    line.value = round2(net + line.tax);
  }

  private get _total(): number {
    return round2(this._lines.reduce((s, l) => s + (Number(l.value) || 0), 0));
  }

  // ── item add ──────────────────────────────────────────────────────────

  private _onSearchInput(e: Event) {
    this._searchTerm = (e.target as HTMLInputElement).value;
    if (this._searchTimer !== undefined) window.clearTimeout(this._searchTimer);
    const term = this._searchTerm.trim();
    if (term.length < 2) {
      this._searchResults = [];
      this._searchOpen = false;
      return;
    }
    this._searchBusy = true;
    this._searchTimer = window.setTimeout(() => void this._runSearch(term), 300);
  }

  private async _runSearch(term: string) {
    const epoch = this._view.epoch;
    try {
      const res = await api.posStockSearch(this.storeCode, term);
      if (!this._view.isCurrent(epoch)) return;
      this._searchResults = res.data ?? [];
      this._searchOpen = this._searchResults.length > 0;
    } catch {
      if (this._view.isCurrent(epoch)) this._searchResults = [];
    } finally {
      if (this._view.isCurrent(epoch)) this._searchBusy = false;
    }
  }

  private async _onSearchKey(e: KeyboardEvent) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const term = this._searchTerm.trim();
    if (!term) return;
    // Exact code first (desktop parity); fall back to text search results
    // so an unresolved code never dead-ends the user.
    this._searchOpen = false;
    const epoch = this._view.epoch;
    try {
      const res = await api.posStockByCode(this.storeCode, term);
      if (!this._view.isCurrent(epoch)) return;
      this._openPick(res.data, res.data.pack);
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._searchBusy = true;
      await this._runSearch(term);
      if (!this._view.isCurrent(epoch)) return;
      if (this._searchOpen && this._searchResults.length > 0) {
        this._error = `No exact code match for '${term}' — pick an item from the results.`;
      } else {
        this._error = e?.message || `Item '${term}' not found`;
      }
    }
  }

  private async _pickSearchResult(item: PosStockItem) {
    this._searchOpen = false;
    const epoch = this._view.epoch;
    try {
      const res = await api.posStockById(this.storeCode, item.id);
      if (!this._view.isCurrent(epoch)) return;
      this._openPick(res.data);
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || 'Failed to load item';
    }
  }

  private _openPick(item: PosStockItem, matchedPack?: string) {
    this._pick = item;
    this._pickQty = '1';
    this._pickDisc = '';
    this._pickBonusQty = '';
    this._pickBatch = item.batchNo || '';
    this._pickExpiry = item.expiryDate ? new Date(item.expiryDate).toISOString().slice(0, 7) : '';
    const packs = item.packRecords ?? [];
    const pack =
      packs.find((p) => matchedPack && p.packTypeCode === matchedPack) ??
      packs.find((p) => p.packTypeCode === item.pack) ??
      packs[0];
    this._pickPack = pack?.packTypeCode || '';
    this._pickPrice = String(this._defaultCost(item));
    this._pickSrp = pack ? String(pack.sellingPrice) : '0';
    this._error = '';
  }

  private _pickPackRecord(): PosPack | undefined {
    const item = this._pick;
    if (!item) return undefined;
    return (item.packRecords ?? []).find((p) => p.packTypeCode === this._pickPack) ?? item.packRecords?.[0];
  }

  private _addPicked() {
    const item = this._pick;
    const pack = this._pickPackRecord();
    if (!item || !pack) return;
    const qty = parseFloat(this._pickQty) || 0;
    if (qty <= 0) {
      this._error = 'Quantity must be greater than zero';
      return;
    }
    const line: RecLine = {
      stockId: item.id,
      code: item.code,
      name: item.name || item.displayName || '',
      pack: pack.packTypeCode,
      packDesc: pack.packDescription || pack.packTypeCode,
      packSize: pack.packSize || 1,
      quantity: qty,
      bonusQty: parseFloat(this._pickBonusQty) || 0,
      price: parseFloat(this._pickPrice) || 0,
      discount: parseFloat(this._pickDisc) || 0,
      srp: parseFloat(this._pickSrp) || 0,
      taxPct: this._taxPct(item),
      tax: 0,
      value: 0,
      batchNo: this._pickBatch,
      expiryMs: this._pickExpiry ? new Date(`${this._pickExpiry}-01T00:00:00`).getTime() : 0,
      qoh: item.qoh ?? 0,
    };
    this._recalc(line);
    this._lines = [...this._lines, line];
    this._pick = null;
    this._searchTerm = '';
    this._searchResults = [];
    this._error = '';
  }

  private _removeLine(ix: number) {
    this._lines = this._lines.filter((_, i) => i !== ix);
  }

  private _updateLine(ix: number, patch: Partial<RecLine>) {
    this._lines = this._lines.map((l, i) => {
      if (i !== ix) return l;
      const next = { ...l, ...patch };
      this._recalc(next);
      return next;
    });
  }

  // ── supplier picker ───────────────────────────────────────────────────

  private async _openParty() {
    this._partyOpen = true;
    this._partySearch = '';
    this._partyResults = [];
    await this._searchParties('');
  }

  private async _searchParties(q: string) {
    const epoch = this._view.epoch;
    this._partyBusy = true;
    try {
      const res = await api.staffCompanies(this.storeCode, 'opscompany', q);
      if (!this._view.isCurrent(epoch)) return;
      this._partyResults = (res.data ?? []).map((r) => ({ id: r.id, description: r.description }));
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Search failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._partyBusy = false;
    }
  }

  private async _chooseParty(opt: PartyOption) {
    const epoch = this._view.epoch;
    this._partyBusy = true;
    try {
      const c = (await api.staffCompanyDetail(this.storeCode, 'opscompany', opt.id)).data;
      if (!this._view.isCurrent(epoch)) return;
      this._supplierId = c.id;
      this._supplierName = c.name;
      this._supplierCode = c.code;
      if (Number(c.creditTerm)) this._term = String(Number(c.creditTerm));
      this._partyOpen = false;
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Failed to load supplier';
    } finally {
      if (this._view.isCurrent(epoch)) this._partyBusy = false;
    }
  }

  // ── save / hold / recall ──────────────────────────────────────────────

  private _buildPayload(): TradePurchaseIn {
    const unchanged = this._loadedTxnMs > 0 && this._date === isoFromMs(this._loadedTxnMs);
    const when = unchanged
      ? this._loadedTxnMs
      : this._date
        ? new Date(`${this._date}T${new Date().toTimeString().slice(0, 8)}`).getTime()
        : undefined;
    return {
      ...(this._editingId ? { id: this._editingId } : {}),
      items: this._lines.map((l) => ({
        stockId: l.stockId,
        pack: l.pack,
        quantity: Number(l.quantity) || 0,
        bonusQty: l.bonusQty || 0,
        price: l.price,
        discount: l.discount || 0,
        srp: l.srp || 0,
        tax: l.tax,
        value: l.value,
        batchNo: l.batchNo || '',
        expiryDate: l.expiryMs || 0,
      })),
      supplierId: this._supplierId || undefined,
      refNo: this._refNo || undefined,
      note: this._note || undefined,
      term: parseFloat(this._term) || 0,
      transactionDate: when,
    };
  }

  private _validate(): string {
    if (this._lines.length === 0) return 'Add at least one item';
    if (!this._supplierId) return 'Select a supplier';
    return '';
  }

  private async _save(hold: boolean) {
    if (hold && this._editingId) {
      this._error = 'Holding is not available while editing a posted receive — post it to update.';
      return;
    }
    const invalid = this._validate();
    if (invalid) {
      this._error = invalid;
      return;
    }
    this._busy = true;
    this._error = '';
    this._notice = '';
    const editing = !!this._editingId;
    this._savedUpdated = false;
    const epoch = this._view.epoch;
    try {
      const payload = this._buildPayload();
      const res = hold
        ? await api.tradeHoldReceive(this.storeCode, payload)
        : await api.tradeReceiveStock(this.storeCode, payload);
      if (!this._view.isCurrent(epoch)) return;
      await this._releaseRecalledHold();
      if (hold) {
        this._notice = `Receive on hold — #${res.data.id}`;
        this._clearAll();
      } else {
        this._savedId = res.data.id;
        this._savedUpdated = editing;
        this._clearAll();
      }
    } catch (e: any) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = e?.message || (hold ? 'Hold failed' : 'Posting failed');
    } finally {
      if (this._view.isCurrent(epoch)) this._busy = false;
    }
  }

  private async _releaseRecalledHold() {
    const holdId = this._recallHoldId;
    this._recallHoldId = null;
    if (!holdId) return;
    try {
      await api.tradeDeletePurchase(this.storeCode, holdId, 'hold');
    } catch {
      // New record saved; the old hold can be removed manually if it lingers.
    }
  }

  private async _downloadPdf(id: string) {
    try {
      const blob = await api.tradePurchasePdf(this.storeCode, id, 'invoice');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receive_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e: any) {
      this._error = e?.message || 'PDF failed';
    }
  }

  private _loadRecall(row: TradePurchaseRow, edit: boolean) {
    const lines: RecLine[] = (row.lines ?? []).map((l) => {
      const line: RecLine = {
        stockId: l.stockId,
        code: l.code,
        name: l.name,
        pack: l.pack,
        packDesc: l.packDesc || l.pack,
        packSize: l.packSize ?? 1,
        quantity: l.quantity,
        bonusQty: l.bonusQty ?? 0,
        price: l.price ?? 0,
        discount: l.discount ?? 0,
        srp: l.srp ?? 0,
        // derive the effective GST rate so changing qty/price on a recalled or
        // edited line keeps charging tax instead of zeroing it
        taxPct: (l.quantity && l.price)
          ? (Number(l.tax) || 0) / Math.max(l.quantity * l.price - (l.discount ?? 0), 0.0001)
          : 0,
        tax: l.tax ?? 0,
        value: l.value ?? 0,
        batchNo: l.batchNo || '',
        expiryMs: l.expiryDate || 0,
        qoh: l.qoh ?? 0,
      };
      return line;
    });
    this._lines = lines;
    this._supplierId = row.supplierId || '';
    this._supplierName = row.supplierName || '';
    this._supplierCode = row.supplierCode || '';
    this._refNo = row.refNo || '';
    this._term = String(row.term || 0);
    this._note = row.note || '';
    // Keep the original transaction date on both recall and edit.
    this._loadedTxnMs = row.date || 0;
    this._date = row.date ? isoFromMs(row.date) : todayIso();
    this._recallHoldId = edit ? null : row.id;
    this._editingId = edit ? row.id : '';
    this._error = '';
    this._notice = edit
      ? `Editing receive #${row.id} — post to update this record`
      : `Loaded on-hold receive #${row.id} — the hold will be released when you post or re-hold`;
  }

  // ── render ────────────────────────────────────────────────────────────

  render() {
    if (!this.storeCode) return html`<div class="empty">No outlet selected.</div>`;
    const ready = !!this._sk && this._skStore === this.storeCode;
    if (!ready && this._skFailed === this.storeCode) {
      return html`
        <div class="empty">
          ⚠ ${this._initError || 'Failed to load store settings'}
          <div style="margin-top:0.75rem">
            <button class="btn btn-sm" @click=${() => { this._skFailed = ''; void this._init(); }}>Retry</button>
          </div>
        </div>`;
    }
    if (!ready) {
      return html`<div class="empty">Loading store settings…</div>`;
    }
    return html`
      <div class="rec-wrap">
        <div class="head-row">
          <span class="party-chip">
            🏭 Supplier: <strong>${this._supplierName || '—'}</strong>
            ${this._supplierId ? html`<button title="Clear"
              @click=${() => { this._supplierId = ''; this._supplierName = ''; this._supplierCode = ''; }}>&times;</button>` : ''}
          </span>
          <button class="btn btn-sm" data-testid="receive-supplier" @click=${() => void this._openParty()}>Select Supplier</button>
          <div class="fg"><label>Invoice/Ref No</label>
            <input .value=${this._refNo} @input=${(e: Event) => { this._refNo = (e.target as HTMLInputElement).value; }}></div>
          <div class="fg" style="max-width:110px"><label>Term</label>
            <input type="number" .value=${this._term} @change=${(e: Event) => { this._term = (e.target as HTMLInputElement).value; }}></div>
          <div class="fg"><label>Date</label>
            <input type="date" .value=${this._date} @change=${(e: Event) => { this._date = (e.target as HTMLInputElement).value; }}></div>
          <div class="fg grow"><label>Note</label>
            <input .value=${this._note} @input=${(e: Event) => { this._note = (e.target as HTMLInputElement).value; }}></div>
        </div>

        <div class="add-row">
          <input placeholder="Scan / type item code (Enter to add) or search…"
            data-testid="receive-search"
            .value=${this._searchTerm}
            @input=${this._onSearchInput}
            @keydown=${this._onSearchKey} />
          ${this._searchBusy ? html`<span style="align-self:center;font-size:0.75rem;color:var(--color-text-secondary)">…</span>` : ''}
          ${this._searchOpen ? html`
            <div class="search-pop">
              ${this._searchResults.map((r) => html`
                <button class="search-item" data-testid="receive-search-result" @click=${() => void this._pickSearchResult(r)}>
                  <span class="si-name">${r.name}${r.displayName && r.displayName !== r.name ? ` · ${r.displayName}` : ''}</span>
                  <span class="si-sub">${r.code} · QOH ${r.qohString ?? r.qoh ?? '—'}</span>
                </button>
              `)}
            </div>
          ` : ''}
        </div>

        ${this._error ? html`<div class="err">⚠ ${this._error}</div>` : ''}
        ${this._notice ? html`<div class="notice">✓ ${this._notice}</div>` : ''}

        <div class="grid-wrap">
          <table>
            <thead>
              <tr>
                <th style="width:90px">Code</th>
                <th>Item</th>
                <th class="num" style="width:80px">Qty</th>
                <th style="width:80px">Pk</th>
                <th class="num" style="width:70px">Bonus</th>
                <th class="num" style="width:90px">Cost</th>
                <th class="num" style="width:80px">Disc</th>
                <th style="width:110px">Batch</th>
                <th style="width:120px">Expiry</th>
                <th class="num" style="width:90px">S/Price</th>
                <th class="num" style="width:90px">Total</th>
                <th style="width:36px"></th>
              </tr>
            </thead>
            <tbody>
              ${this._lines.length === 0 ? html`
                <tr><td colspan="12"><div class="empty">Scan or search an item to start.</div></td></tr>
              ` : this._lines.map((l, ix) => html`
                <tr>
                  <td>${l.code}</td>
                  <td>${l.name}</td>
                  <td class="cell-num"><input type="number" step="0.001" min="0" .value=${String(l.quantity)}
                    @change=${(e: Event) => this._updateLine(ix, { quantity: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                  <td>${l.packDesc}</td>
                  <td class="cell-num"><input type="number" step="0.001" min="0" .value=${String(l.bonusQty || '')}
                    @change=${(e: Event) => this._updateLine(ix, { bonusQty: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                  <td class="cell-num"><input type="number" step="0.01" min="0" .value=${String(l.price)}
                    @change=${(e: Event) => this._updateLine(ix, { price: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                  <td class="cell-num"><input type="number" step="0.01" min="0" .value=${String(l.discount || '')}
                    @change=${(e: Event) => this._updateLine(ix, { discount: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                  <td><input .value=${l.batchNo}
                    @change=${(e: Event) => this._updateLine(ix, { batchNo: (e.target as HTMLInputElement).value })}></td>
                  <td><input type="month" .value=${l.expiryMs ? new Date(l.expiryMs).toISOString().slice(0, 7) : ''}
                    @change=${(e: Event) => {
                      const v = (e.target as HTMLInputElement).value;
                      this._updateLine(ix, { expiryMs: v ? new Date(`${v}-01T00:00:00`).getTime() : 0 });
                    }}></td>
                  <td class="cell-num"><input type="number" step="0.01" min="0" .value=${String(l.srp || '')}
                    @change=${(e: Event) => this._updateLine(ix, { srp: parseFloat((e.target as HTMLInputElement).value) || 0 })}></td>
                  <td class="num">${fmtMoney(l.value)}</td>
                  <td><button class="rm" title="Remove" @click=${() => this._removeLine(ix)}>✕</button></td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>

        <div class="footer-bar">
          <div class="totals-box">
            <div class="t"><span class="lbl">Items</span><span class="val">${this._lines.length}</span></div>
            <div class="t grand"><span class="lbl">Total</span><span class="val">${fmtMoney(this._total)}</span></div>
          </div>
          <button class="btn btn-sec" ?disabled=${this._busy || this._lines.length === 0 || !!this._editingId}
            data-testid="receive-hold"
            title=${this._editingId ? 'Holding is not available while editing a posted receive' : ''}
            @click=${() => void this._save(true)}>
            ${this._busy ? '…' : '⏸ Hold'}
          </button>
          <button class="btn" style="min-height:48px;font-size:0.95rem" data-testid="receive-save"
            ?disabled=${this._busy || this._lines.length === 0} @click=${() => void this._save(false)}>
            ${this._editingId ? '💾 Update Receive' : '📥 Post Receive'}
          </button>
        </div>
      </div>

      ${this._pick ? this._renderPick() : ''}
      ${this._partyOpen ? this._renderParty() : ''}
      ${this._savedId ? this._renderSaved() : ''}
    `;
  }

  private _renderPick() {
    const item = this._pick!;
    const packs = item.packRecords ?? [];
    const singlePack = RETAIL && packs.length === 1;
    return html`
      <div class="modal-overlay" @click=${() => { this._pick = null; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>${item.name}</h3>
            <button class="modal-close" @click=${() => { this._pick = null; }}>&times;</button>
          </div>
          <div class="modal-body">
            <div style="font-size:0.78rem;color:var(--color-text-secondary);margin-bottom:0.75rem">
              ${item.code} · QOH ${item.qohString ?? item.qoh ?? '—'}
              · Avg ${fmtMoney(item.weightedAvgCost)} · Latest ${fmtMoney(item.latestCost)} · Max ${fmtMoney(item.maxCost)}
            </div>
            <div class="pick-grid">
              ${singlePack
                ? ''
                : html`
                    <div class="fg"><label>Pack</label>
                      <select .value=${this._pickPack} @change=${(e: Event) => { this._pickPack = (e.target as HTMLSelectElement).value; }}>
                        ${packs.map((p) => html`
                          <option value=${p.packTypeCode} ?selected=${p.packTypeCode === this._pickPack}>
                            ${p.packDescription || p.packTypeCode}${p.packSize ? ` (x${p.packSize})` : ''}
                          </option>`)}
                      </select></div>`}
              <div class="fg"><label>Quantity</label>
                <input type="number" step="0.001" min="0" .value=${this._pickQty}
                  data-testid="receive-qty"
                  @input=${(e: Event) => { this._pickQty = (e.target as HTMLInputElement).value; }}></div>
              <div class="fg"><label>Cost Price</label>
                <input type="number" step="0.01" min="0" .value=${this._pickPrice}
                  data-testid="receive-price"
                  @input=${(e: Event) => { this._pickPrice = (e.target as HTMLInputElement).value; }}></div>
              <div class="fg"><label>Bonus Qty</label>
                <input type="number" step="0.001" min="0" .value=${this._pickBonusQty}
                  @input=${(e: Event) => { this._pickBonusQty = (e.target as HTMLInputElement).value; }}></div>
              <div class="fg"><label>Disc (amount)</label>
                <input type="number" step="0.01" min="0" .value=${this._pickDisc}
                  @input=${(e: Event) => { this._pickDisc = (e.target as HTMLInputElement).value; }}></div>
              <div class="fg"><label>S/Price</label>
                <input type="number" step="0.01" min="0" .value=${this._pickSrp}
                  @input=${(e: Event) => { this._pickSrp = (e.target as HTMLInputElement).value; }}></div>
              <div class="fg"><label>Batch No</label>
                <input .value=${this._pickBatch}
                  @input=${(e: Event) => { this._pickBatch = (e.target as HTMLInputElement).value; }}></div>
              <div class="fg"><label>Expiry</label>
                <input type="month" .value=${this._pickExpiry}
                  @input=${(e: Event) => { this._pickExpiry = (e.target as HTMLInputElement).value; }}></div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${() => { this._pick = null; }}>Cancel</button>
            <button class="btn-submit" data-testid="receive-add-item" @click=${() => this._addPicked()}>Add Item</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderParty() {
    return html`
      <div class="modal-overlay" @click=${() => { this._partyOpen = false; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>Select Supplier</h3>
            <button class="modal-close" @click=${() => { this._partyOpen = false; }}>&times;</button>
          </div>
          <div class="modal-body">
            <div class="fg">
              <input placeholder="Search supplier name / code…" data-testid="receive-party-search" .value=${this._partySearch}
                @input=${(e: Event) => {
                  this._partySearch = (e.target as HTMLInputElement).value;
                  if (this._searchTimer !== undefined) window.clearTimeout(this._searchTimer);
                  this._searchTimer = window.setTimeout(() => void this._searchParties(this._partySearch.trim()), 300);
                }} />
            </div>
            <div>
              ${this._partyBusy ? html`<div style="padding:0.6rem;font-size:0.8rem;color:var(--color-text-secondary)">Searching…</div>` : ''}
              ${!this._partyBusy && this._partyResults.length === 0 ? html`
                <div style="padding:0.6rem;font-size:0.8rem;color:var(--color-text-secondary)">No matches.</div>` : ''}
              ${this._partyResults.map((r) => html`
                <button class="search-item" data-testid="receive-party-result" @click=${() => void this._chooseParty(r)}>
                  <span class="si-name">${r.description}</span>
                </button>
              `)}
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${() => { this._partyOpen = false; }}>Close</button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderSaved() {
    return html`
      <div class="modal-overlay" data-testid="receive-saved" @click=${() => { this._savedId = ''; }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3>Receive ${this._savedUpdated ? 'updated' : 'posted'} — #${this._savedId}</h3>
            <button class="modal-close" @click=${() => { this._savedId = ''; }}>&times;</button>
          </div>
          <div class="modal-body">
            <div class="pay-line"><span>Result</span><span>Stock quantities, costs and selling prices updated.</span></div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-sec" @click=${() => void this._downloadPdf(this._savedId)}>⬇ PDF</button>
            <button class="btn-submit" data-testid="receive-done" @click=${() => { this._savedId = ''; }}>Done</button>
          </div>
        </div>
      </div>
    `;
  }
}
